// © Microsoft Corporation. All rights reserved.

import { SettablePromise } from "@msnews/experiences-base";

/** Collection of Element with there respective ID */
const elementMap: Map<string, Element> = new Map<string, Element>();

/** Map of element and promise that will resolve when the element is rendered */
const waitForElementMap: Map<string, SettablePromise<Element>> = new Map<string, any>();

/** Insert the Element with the Key in the mapper. If there's a promise for the Element, resolve it. */
export function registerElementByKey(key: string, element: Element) {
    elementMap.set(key, element);

    // Resolve the promise if someone is waiting on it
    const waitForElementPromise = waitForElementMap.get(key);
    if (waitForElementPromise) {
        waitForElementPromise.set(element);
    }
}

/** Get the appropriate element from the Key */
export function getElementByKey(key: string): Element {
    if (!key) {
        return null;
    }
    return elementMap.get(key);
}

/**
 * If key is not available in the mapper return false or true if key is deleted from mapper
 */
export function removeElementByKey(key: string): boolean {
    if (!elementMap.has(key)) {
        return false;
    }

    elementMap.delete(key);

    return true;
}

/**
 * Return a new or existing promise for the given target element that will resolve once the element is registered.
 * @param key Key specifying the target element
 * @returns Promise that will resolve with the target element if found or null otherwise
 */
export function waitForElementByKey(key: string): Promise<Element> {
    if (!key) {
        return Promise.reject(null);
    }

    // If element is already rendered, resolve immediately
    const element = getElementByKey(key);
    if (getElementByKey(key)) {
        return Promise.resolve(element);
    }

    // If a promise is already created, return that
    if (waitForElementMap.has(key)) {
        return waitForElementMap.get(key).getResultAsync();
    }

    // Create a new promise and return it
    const elementPromise = new SettablePromise<Element>();
    waitForElementMap.set(key, elementPromise);
    return elementPromise.getResultAsync();
}